/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Field, Form, Formik } from "formik";
import { GlobalLoaderContext, GlobalAuthContext } from "store/context";
import * as yup from "yup";
import Layout from "components/Layout";
import ContentCard from "components/ContentCard";
import Loader from "components/Loader";
import Row from "components/Grid/Row";
import Col from "components/Grid/Col";
import FormContainer from "components/FormContainer";
import ComboBox from "components/Form/ComboBox3";
import { Button } from "components/index";
import InputField from "components/Form/InputField";
import Colors from "constants/Colors";
import { FitinsurAPI, MessageCenter } from "utils";
import { ButtonContainer, ButtonContent } from "./styles";
import Coverages from "./Coverages";
import Access from 'constants/Access';


const EditProductPlan = ({ match }) => {
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const history = useHistory();
  const formikRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();

  const [idProduct, setIdProduct] = useState(match?.params?.idProduct);
  const [idProductPlan, setIdProductPlan] = useState(match?.params?.idProductPlan);
  const [idPerson, setIdPerson] = useState(match.params.idPerson);
  const [hasPermission, setHasPermission] = useState(true);
  const [changeProductPlanRuleType, setChangeProductPlanRuleType] = useState(false);
  const [validations, setValidations] = useState({});
  const [initial, setInitial] = useState({
    productName: {
      idDomain: "",
      domainDescription: "",
      idDomainType: "",
    },
    productPlanName: "",
    productPlanType: {
      idDomain: "",
      domainDescription: "",
      idDomainType: "",
    },
    quotationExpirePeriod: "",
    vigencyType: {
      idDomain: "",
      domainDescription: "",
      idDomainType: "",
    }
  });
  const [coverages, setCoverages] = useState(null);
  const [formOptions, setFormOptions] = useState({
    productNames: [],
    productPlanTypes: [],
    vigencyType: [],
    coverageTypes: [],
    productPlanRuleType: [],
  });
  const [coveragesByProduct, setCoveragesByProduct] = useState(null);

  const getProductsInfo = async () => {
    try {
      if (!idProduct) return getFormOptions();

      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetInfoProducts(idPerson, idProduct, idProductPlan).then((res) => res.json());
      if (response.ret_code !== 200) mc.error(response.ret_msg_details);

      let {
        productName,
        productPlanName,
        quotationExpirePeriod,
        vigencyType,
        productPlanType,
        coverages,
        coveragesByProduct,
        formOptions,
        productPlanRuleType,
      } = response.ret_value;

      const newProjectNames = formOptions.productNames;
      const newFormOptions = {
        ...formOptions,
        productNames: newProjectNames
      }

      formikRef.current.values.productName = productName || "";
      formikRef.current.values.productPlanName = productPlanName || "";
      formikRef.current.values.quotationExpirePeriod = quotationExpirePeriod || "";
      formikRef.current.values.vigencyType = vigencyType || "";
      formikRef.current.values.productPlanType = productPlanType || "";
      formikRef.current.values.productPlanRuleType = productPlanRuleType || "";

      setCoveragesByProduct(coveragesByProduct);
      setCoverages(coverages || []);
      setFormOptions(newFormOptions);
      loading.stop();
    } catch (error) {
      console.log("Error fetch data: ", error);
      loading.stop();
      mc.error("An error happened, please try again.");
    }
  };

  const getFormOptions = async () => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetProductFormOptions()
        .then(res => res.json());

      if (response.ret_code != 200) {
        loading.stop();
        mc.error(response.ret_msg_details);
      }

      const newProjectNames = response.ret_value.productNames;
      const newFormOptions = {
        ...response.ret_value,
        productNames: newProjectNames
      }

      loading.stop();
      setFormOptions(newFormOptions);
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  async function loadContent() {
    await createValidation();
    await getProductsInfo();
    await permissionAccess();
    loading.stop();
  }

  useEffect(() => {
    loadContent();
  }, [initial])

  useEffect(() => {
    setIdProduct(match?.params?.idProduct);
    setIdPerson(match?.params?.idPerson);
    loadContent();
  }, [match.params.idProduct])
  
  
  const getFiledOptions = function (answerOptions, product, isCreating) {
    return answerOptions.map(option => {
      if (isCreating) {
        return {
          label: option.description,
          value: {
            idDomain: option.idDomain,
            description: option.description,
            idDomainType: option.idDomainType,
            idProduct: option?.idProduct
          }
        };
      } else if (option.idDomain) {
        return {
          label: option.description,
          value: {
            idDomain: option.idDomain,
            description: option.description,
            idDomainType: option.idDomainType,
          }
        };
      } else if (product) {
        return {
          label: option.description,
          value: {
            description: option.description,
            idDomain: option.idDomain
          }
        }
      }
    });
  }

  async function permissionAccess() {
    try {
      const response = await fitinsur
        .requestAccess("", idPerson ? "UPDATE" : "CREATE", Access.PERSON_EDIT, null, null, authContext.token)
        .then((response) => response.json())
      const hasPermission = response && response.ret_code === 200;
      setHasPermission(hasPermission);
    } catch (e) {
      setHasPermission(false);
    }
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      productName: yup.string().required("* Required field"),
      productPlanName: yup.string().required("* Required field"),
      productPlanType: yup.string().required("* Required field"),
      quotationExpirePeriod: yup.string().required("* Required field"),
      vigencyType: yup.string().required("* Required field"),
      productPlanRuleType: changeProductPlanRuleType ? yup.string().required("* Required field") : '',
    });
    setValidations(validations);
    return validations;
  };


  const saveProduct = async (body) => {
    try {
      if (idPerson) body.idPerson = idPerson;
      if (idProduct) {
        body.idProduct = idProduct;
        body.idProductPlan = idProductPlan;
      }

      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSaveProductInfo(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      mc.success("Registration successful!");
      loading.stop();
      history.push(`/product-plan/person/${idPerson}`);
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const renderOptionSave = (values) => {
    return (
      <Button
        fullWidth
        type="submit"
        primary
        onClick={() => {
          createValidation().validate(values).catch((e) => {
            console.log('validation error', e)
            mc.error('Please fill in all required fields.')
          })
        }}
        id="btnSubmit"
      >
        ADD PRODUCT PLAN
      </Button>
    );
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveProduct(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <Layout title={"New Register"}>
          <ContentCard title="PRODUCT PLAN INFORMATION">
            <Form>
              <Row>
                <Col lg={4} md={4} sm={4} xs={12}>
                  <Field
                    name="productName"
                    label="Product"
                    options={getFiledOptions(formOptions.productNames, null, idProduct ? false : true)}
                    onChange={event => setFieldValue('productName', event)}
                    component={ComboBox}
                    value={values.productName}
                    isRequired
                    disabled={idProduct}
                  />
                </Col>
                {
                  values.productName.idProduct != 36 ?
                    setChangeProductPlanRuleType(false) : null
                }
                <Col lg={4} md={4} sm={4} xs={12}>
                  <Field
                    name="productPlanName"
                    label="Product Plan Name"
                    isRequired
                    onChange={event => setFieldValue('productPlanName', event)}
                    component={InputField}
                    values={values.productPlanName}
                  />
                </Col>

                <Col lg={4} md={4} sm={4} xs={12}>
                  <Field
                    name="productPlanType"
                    label="Product Plan Type"
                    options={getFiledOptions(formOptions.productPlanTypes)}
                    onChange={event => setFieldValue('productPlanType', event)}
                    component={ComboBox}
                    values={values.productPlanType}
                    isRequired
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={4} md={4} sm={4} xs={12}>
                  <Field
                    name="quotationExpirePeriod"
                    label="Quotation Expire Period (days)"
                    isRequired
                    onChange={event => setFieldValue('quotationExpirePeriod', event)}
                    component={InputField}
                    values={values.quotationExpirePeriod}
                    type="Number"
                  />
                </Col>

                <Col lg={4} md={4} sm={4} xs={12}>
                  <Field
                    name="vigencyType"
                    label="Vigency type"
                    isRequired
                    options={getFiledOptions(formOptions.vigencyType, true)}
                    onChange={event => setFieldValue('vigencyType', event)}
                    component={ComboBox}
                    value={values.vigencyType}
                  />
                </Col>

                {
                  !idProduct && values.productName.idProduct == 36 ?
                    <Col lg={4} md={4} sm={4} xs={12}>
                      <Field
                        name="productPlanRuleType"
                        label="Product Plan Rule Type"
                        isRequired
                        options={[{
                          label: 'Worldwide',
                          value: {
                            description: 'Worldwide',
                            idDomain: 4689
                          }
                        }, {
                          label: 'MAS',
                          value: {
                            description: 'MAS',
                            idDomain: 4690
                          }
                        }]}
                        onChange={event => setFieldValue('productPlanRuleType', event)}
                        onLoad={setChangeProductPlanRuleType(true)}
                        component={ComboBox}
                        value={values.productPlanRuleType}
                      />
                    </Col>
                    : Number(idProduct) === 36 ?
                    <Col lg={4} md={4} sm={4} xs={12}>
                      <Field
                        name="productPlanRuleType"
                        label="Product Plan Rule Type"
                        isRequired
                        options={[{
                          label: 'Worldwide',
                          value: {
                            description: 'Worldwide',
                            idDomain: 4689
                          }
                        }, {
                          label: 'MAS',
                          value: {
                            description: 'MAS',
                            idDomain: 4690
                          }
                        }]}
                        onChange={event => setFieldValue('productPlanRuleType', event)}
                        onLoad={setChangeProductPlanRuleType(true)}
                        component={ComboBox}
                        value={values.productPlanRuleType}
                      />
                    </Col>
                    : null
                }
              </Row>


              {idProduct &&
                <ButtonContainer>
                  <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }}>
                    <ButtonContent />
                  </ButtonContainer>
                  <ButtonContent>
                    <Button
                      fullWidth
                      type="submit"
                      primary
                      onClick={() => {
                        createValidation().validate(values).catch((e) => {
                          console.log('validation error', e)
                          mc.error('Please fill in all required fields.')
                        })
                      }}
                      id="btnSubmit">
                      SAVE
                    </Button>
                  </ButtonContent>
                </ButtonContainer>}

              {idProduct &&
                <Row style={{ marginTop: '30px' }}>
                  <Col lg={12} md={12} sm={12} xs={12}>
                    <FormContainer show={true} title="Coverages">
                      <Coverages
                        hasPermission={hasPermission}
                        typeProductPlan={values.productPlanType}
                        coverages={coverages}
                        formOptions={formOptions}
                        productPlan={idProductPlan}
                        idProduct={idProduct}
                        onSaveCoverage={async () => await getProductsInfo()}
                      />
                    </FormContainer>
                  </Col>
                </Row>}
              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }}>
                  <ButtonContent>
                    <Button forth onClick={() => history.goBack()} style={{ alignSelf: 'flex-start' }}> &lt; Back</Button>
                  </ButtonContent>
                </ButtonContainer>
                {!idProduct && renderOptionSave(values)}
              </ButtonContainer>
              {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
            </Form>
          </ContentCard>
        </Layout>
      )}
    </Formik>
  );
};

export default EditProductPlan;
